import { AddToFilter } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipActions/AddToFilter';
import { EditReason } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipActions/EditReason';
import { MergeWithAnotherReason } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipActions/MergeWithAnotherReason';
import { MoreChartActionsButton } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipActions/MoreChartActionsButton';
import { ViewFeedback } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipActions/ViewFeedback';
import { useChartTooltipStore } from '@modules/charts/components/ChartTooltipWrapper/hooks';
import { useMergeSuggestions } from '@modules/charts/components/ChartTooltipWrapper/hooks/useMergeSuggestions';
import { getChartLabelIdOrName } from '@modules/charts/utils';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import {
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
  toSentimentName,
} from '@modules/common/utils';
import { CompareAddToFilterAction } from '@modules/quantify/components/CompareChartTooltip/CompareAddToFilterAction';
import { QuantifyFeedbackListDrawer } from '@modules/quantify/components/QuantifyFeedbackListDrawer';
import { CompareViewFeedbackAction } from '@modules/quantify/components/QuantifyReportTableActions/CompareViewFeedbackAction';
import {
  useFetchQuantifyReport,
  useGetQuantifyCompareQuery,
  useGetQuantifyDuration,
  useGetQuantifyWhereClause,
  useIsMergeWithAllSelectedReasonEnabled,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareQueryContainer,
  getHasMetadataFilterForEqlField,
} from '@modules/quantify/utils';
import { getMetricKeyID } from '@modules/quantify/utils/getMetricKeyID';
import { MergeSuggestionDetailsDrawer } from '@modules/taxonomy/components/MergeSuggestionDetailsDrawer';
import { EditReasonDrawer } from '@modules/taxonomy/components/Reason/EditReasonDrawer';
import { MoreVertRounded } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  IconButton,
  IconButtonProps,
  MenuList,
  Paper,
  Popper,
  alpha,
} from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { joinEntitySentimentPair } from 'components/QueryBuilderView/utils/entitySentimentWidgetHelper';
import {
  PermissionOperation,
  PermissionResource,
  TimeUnit,
} from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { decodeSourceFromString } from 'v2/core/services/queryBuilder/utils';
import {
  EmptyWhereClause,
  OperationType,
} from 'v2/core/services/queryBuilder/whereClause';
import {
  entitySentimentWhereClauseGenerator,
  getWhereClauseForFieldName,
} from 'v2/core/services/queryBuilder/whereClauseGenerator';
import { shallow } from 'zustand/shallow';
import { MergeWithAllSelectedReasonAction } from './MergeWithAllSelectedReasonAction';

interface QuantifyReportTableActionsProps {
  params: GridRenderCellParams;
  buttonSize?: IconButtonProps['size'];
  shouldShowActionsOnHover?: boolean;
  shouldShowAddToFilterForBothCompareFields?: boolean;
  shouldShowMergeWithSelectedReasonsAction?: boolean;
}

export const QuantifyReportTableActions = ({
  params,
  buttonSize,
  shouldShowActionsOnHover,
  shouldShowAddToFilterForBothCompareFields,
  shouldShowMergeWithSelectedReasonsAction = true,
}: QuantifyReportTableActionsProps) => {
  const actionWrapperRef = useRef<HTMLDivElement>(null);

  const fetchQuantifyReport = useFetchQuantifyReport();

  const whereClause = useGetQuantifyWhereClause();
  const compareQuery = useGetQuantifyCompareQuery();
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const duration = useGetQuantifyDuration();
  const query = useQuantifyStore(({ query }) => query);
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.formulaQuery?.criteria,
  );

  const isMergeWithAllSelectedReasonEnabled =
    useIsMergeWithAllSelectedReasonEnabled();

  const setData = useChartTooltipStore(({ setData }) => setData);
  const {
    shouldOpenFeedbackDrawer,
    shouldShowConfirmMergeDrawer,
    shouldShowEditReasonDrawer,
    activeMergeSuggestion,
    activeReason,
  } = useChartTooltipStore(
    ({
      shouldOpenFeedbackDrawer,
      shouldShowConfirmMergeDrawer,
      shouldShowEditReasonDrawer,
      activeMergeSuggestion,
      activeReason,
    }) => ({
      shouldOpenFeedbackDrawer,
      shouldShowConfirmMergeDrawer,
      shouldShowEditReasonDrawer,
      activeMergeSuggestion,
      activeReason,
    }),
    shallow,
  );

  const fieldName = groupBy ? getFieldNameFromEqlField(groupBy) : undefined;
  const qbField = groupBy ? getMetadataQBFieldFromEqlField(groupBy) : undefined;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSelectedCompareFieldA, setIsSelectedCompareFieldA] = useState(false);
  const [selectedFeedbackQueryContainer, setSelectedFeedbackQueryContainer] =
    useState<QueryContainer | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);

    const dataPointWhereClause = (() => {
      if (!rowData || !fieldName) {
        return EmptyWhereClause;
      }

      if (rowData.entitySentiment) {
        return entitySentimentWhereClauseGenerator([
          joinEntitySentimentPair(toSentimentName(rowData.entitySentiment), id),
        ]);
      }

      return getWhereClauseForFieldName({
        fieldName,
        values:
          fieldName === FieldNames.Source
            ? [decodeSourceFromString(rowData?.id)]
            : [id],
        op: OperationType.AND,
        qbField,
      });
    })();
    const unionOfDataPointAndBaseWhereClause = combineWhereClauses(
      whereClause,
      dataPointWhereClause,
      OperationType.AND,
    );
    const feedbackQueryContainer = selectedFeedbackQueryContainer ?? {
      AggregatePeriod: TimeUnit.Week,
      DateRange: duration as TimeRange,
      WhereClause: measureAsCriteriaWhereClause
        ? combineWhereClauses(
            unionOfDataPointAndBaseWhereClause,
            measureAsCriteriaWhereClause,
            OperationType.AND,
          )
        : unionOfDataPointAndBaseWhereClause,
    };

    setSelectedFeedbackQueryContainer(feedbackQueryContainer);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    setData({
      payload: params.row,
      qbField,
      fieldName,
    });
  }, [params.row, setData, qbField, fieldName]);

  useEffect(() => {
    if (
      !Boolean(anchorEl) ||
      shouldOpenFeedbackDrawer ||
      shouldShowConfirmMergeDrawer ||
      shouldShowEditReasonDrawer
    ) {
      handleClose();
    }
  }, [
    anchorEl,
    handleClose,
    shouldOpenFeedbackDrawer,
    shouldShowConfirmMergeDrawer,
    shouldShowEditReasonDrawer,
  ]);

  const rowData = params.row;
  const id = getChartLabelIdOrName(rowData?.id);

  const onViewCompareFeedback = ({ isFieldA }: { isFieldA: boolean }) => {
    setIsSelectedCompareFieldA(isFieldA);
    const queryContainer = getCompareQueryContainer({
      query,
      isFieldA,
      metricKeyId: getMetricKeyID({
        id: rowData?.id,
        field: fieldName,
      }),
      metricCategory: rowData.category,
      whereClause,
      compareQuery,
      duration,
    });
    setSelectedFeedbackQueryContainer(queryContainer);
    setData({
      shouldOpenFeedbackDrawer: true,
    });
  };

  const measureAsCriteriaWhereClause =
    getHasMetadataFilterForEqlField(measuredAsCriteria);

  const open = Boolean(anchorEl);

  const {
    onAddSuggestion,
    onRemoveSuggestion,
    onUpdateCategory,
    onUpdateSuggestedName,
  } = useMergeSuggestions();

  const canEditReasons = useIsAuthorized({
    resource: PermissionResource.Prediction,
    operation: PermissionOperation.Write,
  });
  const shouldShowReasonActions = !params.row.isInvisible && canEditReasons;
  const shouldShowMergeWithSelectedReasons =
    isMergeWithAllSelectedReasonEnabled &&
    shouldShowMergeWithSelectedReasonsAction;

  return (
    <Box ref={actionWrapperRef}>
      <IconButton
        id="quantify-action-btn"
        aria-controls={open ? 'quantify-action-btn' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        className="js-quantify-action-btn"
        size={buttonSize}
        sx={{
          opacity: shouldShowActionsOnHover ? 0 : 1,
          bgcolor: (theme) =>
            open ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
        }}
        color={open ? 'primary' : 'default'}
      >
        <MoreVertRounded />
      </IconButton>
      <ClickAwayListener
        onClickAway={(event: MouseEvent | TouchEvent) => {
          if (!open) {
            return;
          }
          if (actionWrapperRef.current?.contains(event.target as HTMLElement)) {
            return;
          }
          handleClose();
        }}
      >
        <Popper
          id="quantify-action-btn"
          anchorEl={anchorEl}
          open={open}
          placement="left-start"
        >
          <Paper elevation={8}>
            <MenuList>
              {compareQuery ? (
                <CompareAddToFilterAction
                  isMenuItem
                  metricKeyId={getChartLabelIdOrName(rowData.id)}
                  metricCategory={rowData.category}
                  shouldShowAddToFilterForBothCompareFields={
                    shouldShowAddToFilterForBothCompareFields
                  }
                />
              ) : (
                <AddToFilter isMenuItem />
              )}
              {compareQuery ? (
                <CompareViewFeedbackAction
                  onViewFeedback={onViewCompareFeedback}
                />
              ) : (
                <ViewFeedback isMenuItem />
              )}
              {shouldShowReasonActions && (
                <MoreChartActionsButton isMenuItem>
                  <Box py={0.5}>
                    <MergeWithAnotherReason isMenuItem />
                    {shouldShowMergeWithSelectedReasons && (
                      <MergeWithAllSelectedReasonAction />
                    )}
                    <EditReason isMenuItem />
                  </Box>
                </MoreChartActionsButton>
              )}
            </MenuList>
          </Paper>
        </Popper>
      </ClickAwayListener>
      {rowData && selectedFeedbackQueryContainer && (
        <QuantifyFeedbackListDrawer
          open={shouldOpenFeedbackDrawer}
          onClose={() => setData({ shouldOpenFeedbackDrawer: false })}
          queryContainer={selectedFeedbackQueryContainer}
          metricKeyLabel={getChartLabelIdOrName(rowData.label)}
          metricKeyId={getChartLabelIdOrName(rowData.id)}
          metricKeyCategory={rowData.category}
          isCompareFieldA={isSelectedCompareFieldA}
          isMetricKeyInvisible={rowData?.isInvisible ?? false}
          isMetricKeyUpdateInProgress={rowData?.isUpdateInProgress ?? false}
        />
      )}
      {shouldShowConfirmMergeDrawer && !!activeMergeSuggestion && (
        <MergeSuggestionDetailsDrawer
          activeMergeSuggestion={activeMergeSuggestion}
          closeDrawer={() => setData({ shouldShowConfirmMergeDrawer: false })}
          title="Merge Reasons"
          onUpdateSuggestedName={onUpdateSuggestedName}
          onUpdateCategory={onUpdateCategory}
          onAddSuggestion={onAddSuggestion}
          onRemoveSuggestion={onRemoveSuggestion}
          onMergeComplete={() => fetchQuantifyReport()}
          drawerSource="Quantify Table"
        />
      )}
      {shouldShowEditReasonDrawer && activeReason && (
        <EditReasonDrawer
          isOpen={shouldShowEditReasonDrawer}
          onClose={() => {
            setData({
              activeReason: null,
              shouldShowEditReasonDrawer: false,
            });
          }}
          reasonID={activeReason.id}
          activeReasonData={activeReason}
        />
      )}
    </Box>
  );
};
